import { Plus } from "@gravity-ui/icons";
import { Button, Flex, Icon, Text, TextInput } from "@gravity-ui/uikit";
import { useState } from "react";

import type { TemplateCollectionVariable } from "./TemplatesForm.types";
import css from "./VariableSidebar.module.scss";

interface Props {
  collections: TemplateCollectionVariable[];
  disabled: boolean;
  onInsert: (placeholder: string) => void;
}

// Секция «Динамические поля» с inline-инпутами для ввода имени поля
// (params.<имя> / properties.<имя>). Поле имени локальное на компонент —
// после успешной вставки очищается; не глобальный state.
export const CollectionVariableSection = ({
  collections,
  disabled,
  onInsert,
}: Props) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const setDraft = (key: string, value: string) =>
    setDrafts((prev) => ({ ...prev, [key]: value }));

  const handleInsert = (variable: TemplateCollectionVariable, raw: string) => {
    const name = raw.trim();
    if (!name) return;
    onInsert(`{{${variable.prefix}.${name}}}`);
    setDraft(variable.key, "");
  };

  if (collections.length === 0) return null;

  return (
    <section className={css.section}>
      <div className={css.sectionTitle}>Динамические поля</div>
      <Text variant="caption-2" color="hint" className={css.sectionIntro}>
        Это переменные с произвольным именем — оно зависит от конкретного
        товара. Пригодятся, когда нужно подставить значение из{" "}
        <b>характеристик</b> карточки ({"{{params.Цвет}}"},{" "}
        {"{{params.Размер}}"}) или из <b>свойств</b> товара (
        {"{{properties.Материал}}"}). Если у товара такого поля нет — сработает
        Запасной текст. Регистр и пробелы не важны.
      </Text>
      <Flex direction="column" gap={3}>
        {collections.map((variable) => {
          const draft = drafts[variable.key] ?? "";
          return (
            <div key={variable.key} className={css.collection}>
              <div className={css.collectionHeader}>
                <Text variant="body-2">{variable.label}</Text>
                <Text variant="caption-2" color="hint">
                  {variable.example ?? variable.placeholderTemplate}
                </Text>
              </div>
              <Flex gap={2}>
                <TextInput
                  size="m"
                  placeholder="Имя (Цвет, Материал…)"
                  value={draft}
                  disabled={disabled}
                  onUpdate={(next) => setDraft(variable.key, next)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleInsert(variable, draft);
                    }
                  }}
                />
                <Button
                  size="m"
                  view="outlined-action"
                  disabled={disabled || !draft.trim()}
                  onClick={() => handleInsert(variable, draft)}
                  aria-label={`Вставить ${variable.label}`}
                >
                  <Icon data={Plus} size={14} />
                </Button>
              </Flex>
            </div>
          );
        })}
      </Flex>
    </section>
  );
};
